import React from 'lib/react';
import {
  EVButton, EVGrid, EVInputLabel, EVRadio, EVTypography, EVTextField, EVFormHelperText,
} from 'lib/components';
import { LAYOUT_OPTIONS } from 'features/orderPreferences/OrderPreference.constants';
import { IMAGE_FILES_PATH } from 'constants/AppConstants';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import LinearProgressWithLabel from 'commonComponents/progress-bar';
import ColorPicker from './ColorPicker.component';
import CustomCoverStyles from '../CustomCoverStyles';

function PageLayout({
  logoFile,
  coverColor,
  selectedLayout,
  handleCoverColorChange,
  handleLayoutChange,
  handleLogoFileUpload,
  uploadingLogoFile,
  uploadLogoFileError,
}) {
  const classes = CustomCoverStyles();
  const { t } = useTranslation();

  return (
    <EVGrid container direction="column" style={{ flex: 1, gap: 24 }}>
      <EVGrid container direction="column" style={{ gap: 8 }}>
        <EVTypography className={classes.coverPageInputLabels}>
          {t('order_preferences__cover_page__page_layout')}
        </EVTypography>
        <EVGrid container wrap="nowrap" className={classes.pageLayoutOptions}>
          {Object.values(LAYOUT_OPTIONS).map((layout) => (
            <EVGrid
              key={layout} // #genai - Keep each layout option stable across renders.
              container
              direction="column"
              alignItems="flex-start"
              style={{ gap: 8, width: 'fit-content' }}
            >
              <EVInputLabel htmlFor={`layout${layout}-radio`}>
                <img src={`${IMAGE_FILES_PATH}/custom_cover_layout${layout}.svg`} alt={layout} />
              </EVInputLabel>
              <EVRadio
                id={`layout${layout}-radio`}
                checked={Number(selectedLayout) === layout}
                onChange={handleLayoutChange}
                value={layout}
                color="primary"
                style={{ padding: 0, width: 'fit-content' }}
                name="layout"
              />
            </EVGrid>
          ))}
        </EVGrid>
      </EVGrid>
      <EVGrid container direction="column" style={{ gap: 8 }}>
        <EVTypography className={classes.descriptionText}>{t('order_preferences__cover_page__change_selected_color')}</EVTypography>
        <EVGrid container direction="column" style={{ gap: 8 }}>
          <EVTypography className={classes.coverPageInputLabels}>{t('order_preferences__cover_page__cover_color')}</EVTypography>
          <ColorPicker color={coverColor} setColor={handleCoverColorChange} />
        </EVGrid>
      </EVGrid>
      <EVGrid container direction="column" style={{ gap: 8 }}>
        <EVTypography className={classes.coverPageInputLabels}>Logo</EVTypography>
        <img src={logoFile.data || `${IMAGE_FILES_PATH}/image_placeholder.svg`} alt="logo" className={classes.logoImage} />
        <EVTypography className={classes.descriptionText}>
          {t('order_preferences__cover_page__accept_logo_file_formats_key')}
          <span className="value">{t('order_preferences__cover_page__accept_logo_file_formats_value')}</span>
          <br />
          {t('order_preferences__cover_page__recommended_logo_file_size_key')}
          <span className="value">{t('order_preferences__cover_page__recommended_logo_file_size_value')}</span>
        </EVTypography>
        <EVTextField
          type="file"
          id="logo-file-input"
          style={{ display: 'none' }}
          inputProps={{ accept: 'image/png, image/gif, image/jpeg' }}
          onChange={handleLogoFileUpload}
          disabled={uploadingLogoFile}
        />
        {uploadingLogoFile ? <LinearProgressWithLabel labels={[`Uploading ${logoFile.name}`]} className={classes.linearProgressContainer} />
          : (
            <EVInputLabel htmlFor="logo-file-input" className={classes.logoFileInputLabel}>
              <EVButton
                variant="outlined"
                component="div"
                style={{ padding: '0 16px' }}
              >
                {t('order_preferences__cover_page__choose_file')}
              </EVButton>
              <EVTypography className={classes.logoFileName}>{logoFile.name || t('order_preferences__cover_page__no_file_selected')}</EVTypography>
            </EVInputLabel>
          )}
        {
          uploadLogoFileError && (
            <EVFormHelperText className={classes.logoFileUploadError}>{t('order_preferences__cover_page__logo_upload_error')}</EVFormHelperText>)
        }
      </EVGrid>
    </EVGrid>
  );
}

PageLayout.defaultProps = {
  uploadingLogoFile: false,
  uploadLogoFileError: false,
};

PageLayout.propTypes = {
  logoFile: PropTypes.shape({
    data: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  coverColor: PropTypes.string.isRequired,
  selectedLayout: PropTypes.number.isRequired,
  handleCoverColorChange: PropTypes.func.isRequired,
  handleLayoutChange: PropTypes.func.isRequired,
  handleLogoFileUpload: PropTypes.func.isRequired,
  uploadingLogoFile: PropTypes.bool,
  uploadLogoFileError: PropTypes.bool,
};

export default PageLayout;
